import MDBox from "components/MDBox";
import { useMemo, useState } from "react";
import { countActiveDetections, groupDetectionsByCategory } from "../aiMappingDetections";
import { getCategoryColor, getCategoryLabel } from "../aiMappingColors";
import { AiMappingDetectionLike, AiMappingSegmentationLike } from "../aiMappingTypes";
import { AiMappingController } from "../useAiMappingController";
import AiMappingTaskProgress from "./AiMappingTaskProgress";
import AiMappingDetectionsSection from "./AiMappingDetectionsSection";
import AiMappingResultsHeader from "./AiMappingResultsHeader";
import AiMappingResultsPopovers from "./AiMappingResultsPopovers";
import AiMappingSegmentationsSection from "./AiMappingSegmentationsSection";
import { QuickAddBanner, taskKindLabel } from "./aiMappingResultsShared";

interface AiMappingResultsProps {
  controller: AiMappingController;
}

export default function AiMappingResults({ controller }: AiMappingResultsProps) {
  const { results } = controller;
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>({});
  const [sectionsOpen, setSectionsOpen] = useState({ detections: false, segmentations: false });
  const [editTarget, setEditTarget] = useState<{
    anchor: HTMLElement;
    detection: AiMappingDetectionLike;
  } | null>(null);
  const [editCategory, setEditCategory] = useState("");
  const [editColor, setEditColor] = useState("");
  const [bulkColorAnchor, setBulkColorAnchor] = useState<HTMLElement | null>(null);
  const [bulkColor, setBulkColor] = useState("");
  const [bulkColorTargets, setBulkColorTargets] = useState<AiMappingSegmentationLike[]>([]);
  const [segEditTarget, setSegEditTarget] = useState<{
    anchor: HTMLElement;
    segmentation: AiMappingSegmentationLike;
  } | null>(null);
  const [segEditCategory, setSegEditCategory] = useState("");
  const [segEditColor, setSegEditColor] = useState("");

  const groups = useMemo(
    () => (results ? groupDetectionsByCategory(results.detections) : []),
    [results]
  );
  const total = useMemo(() => (results ? countActiveDetections(results.detections) : 0), [results]);
  const activeSegmentations = useMemo(
    () => (results ? results.segmentations.filter((seg) => !seg.isDeleted) : []),
    [results]
  );
  const checkedSegmentations = useMemo(
    () => activeSegmentations.filter((seg) => controller.checkedSegmentationIds.includes(seg.id)),
    [activeSegmentations, controller.checkedSegmentationIds]
  );

  const segmentationGroups = useMemo(() => {
    const grouped = new Map<
      string,
      { color: string; categories: Set<string>; count: number; sqft: number }
    >();
    activeSegmentations.forEach((segmentation) => {
      const color = getCategoryColor(segmentation.category, segmentation.color);
      const existing = grouped.get(color);
      if (existing) {
        existing.categories.add(segmentation.category);
        existing.count += 1;
        existing.sqft += segmentation.areaSqft ?? 0;
      } else {
        grouped.set(color, {
          color,
          categories: new Set([segmentation.category]),
          count: 1,
          sqft: segmentation.areaSqft ?? 0,
        });
      }
    });

    return Array.from(grouped.values())
      .map((group) => ({
        ...group,
        categoryLabel: Array.from(group.categories)
          .map((category) => getCategoryLabel(category))
          .join(", "),
      }))
      .sort((a, b) => b.count - a.count || a.color.localeCompare(b.color));
  }, [activeSegmentations]);

  const areaId = results?.area.id ?? controller.selectedAreaId ?? "";
  const taskRunning = controller.activeTask?.areaId === areaId;
  const quickAddActive = !!controller.quickAddCategory && controller.quickAddAreaId === areaId;

  const saveEdit = async () => {
    if (!editTarget) return;
    const detection = editTarget.detection;
    setEditTarget(null);
    await controller.updateDetection(areaId, detection.id, {
      category: editCategory,
      color: editColor,
    });
  };

  const saveSegEdit = async () => {
    if (!segEditTarget) return;
    const segmentation = segEditTarget.segmentation;
    setSegEditTarget(null);
    await controller.updateSegmentation(areaId, segmentation.id, {
      category: segEditCategory,
      color: segEditColor,
    });
  };

  return (
    <MDBox display="flex" flexDirection="column" gap={0.75} data-testid="ai-mapping-results">
      <AiMappingResultsHeader controller={controller} areaId={areaId} taskRunning={taskRunning} />

      {taskRunning && controller.activeTask && (
        <AiMappingTaskProgress
          label={taskKindLabel(controller.activeTask.kind)}
          snapshot={controller.taskSnapshot}
          onCancel={controller.cancelTask}
        />
      )}

      {quickAddActive && <QuickAddBanner controller={controller} />}

      <AiMappingDetectionsSection
        controller={controller}
        areaId={areaId}
        groups={groups}
        total={total}
        sectionsOpen={sectionsOpen.detections}
        onToggleSection={() =>
          setSectionsOpen((current) => ({ ...current, detections: !current.detections }))
        }
        expandedGroups={expandedGroups}
        onToggleGroup={(category) =>
          setExpandedGroups((current) => ({ ...current, [category]: !current[category] }))
        }
        quickAddActive={quickAddActive}
        onOpenEdit={(event, detection) => {
          setEditCategory(detection.category);
          setEditColor(detection.color ?? getCategoryColor(detection.category));
          setEditTarget({ anchor: event.currentTarget, detection });
        }}
      />

      <AiMappingSegmentationsSection
        controller={controller}
        areaId={areaId}
        activeSegmentations={activeSegmentations}
        segmentationGroups={segmentationGroups}
        checkedSegmentations={checkedSegmentations}
        sectionsOpen={sectionsOpen.segmentations}
        onToggleSection={() =>
          setSectionsOpen((current) => ({ ...current, segmentations: !current.segmentations }))
        }
        onOpenBulkColor={(event) => {
          setBulkColorTargets(
            checkedSegmentations.length ? [...checkedSegmentations] : [...activeSegmentations]
          );
          setBulkColor(
            checkedSegmentations[0]?.color ??
              activeSegmentations[0]?.color ??
              getCategoryColor(
                checkedSegmentations[0]?.category ?? activeSegmentations[0]?.category ?? ""
              )
          );
          setBulkColorAnchor(event.currentTarget);
        }}
        onOpenGroupColor={(color, event) => {
          const members = activeSegmentations.filter(
            (segmentation) => getCategoryColor(segmentation.category, segmentation.color) === color
          );
          setBulkColorTargets(members);
          setBulkColor(color);
          setBulkColorAnchor(event.currentTarget);
        }}
      />

      <AiMappingResultsPopovers
        controller={controller}
        areaId={areaId}
        checkedSegmentations={checkedSegmentations}
        activeSegmentations={activeSegmentations}
        bulkColorAnchor={bulkColorAnchor}
        bulkColor={bulkColor}
        colorTargets={bulkColorTargets}
        onBulkColorChange={setBulkColor}
        onCloseBulkColor={() => setBulkColorAnchor(null)}
        editTarget={editTarget}
        editCategory={editCategory}
        editColor={editColor}
        onEditCategoryChange={setEditCategory}
        onEditColorChange={setEditColor}
        onCloseEdit={() => setEditTarget(null)}
        onSaveEdit={saveEdit}
        segEditTarget={segEditTarget}
        segEditCategory={segEditCategory}
        segEditColor={segEditColor}
        onSegEditCategoryChange={setSegEditCategory}
        onSegEditColorChange={setSegEditColor}
        onCloseSegEdit={() => setSegEditTarget(null)}
        onSaveSegEdit={saveSegEdit}
      />
    </MDBox>
  );
}
